import { useEffect, useState } from "react";
import api from "../../lib/api";
import { apiError, reportParams } from "../../lib/finance";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";

// Downloads one CSV export. The server journals every export and checks both `finance:export` and the right to see
// its content, so the choices offered here only mirror that.
const EXPORT_NAMES = { expenses: "depenses", contributions: "contributions", reimbursements: "remboursements" };

export async function downloadExport(kind, filters) {
  try {
    const { data } = await api.get(`/finance/exports/${kind}.csv`, { params: reportParams(filters), responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    const suffix = [filters.preconstitution && "avant-constitution", filters.dateFrom, filters.dateTo].filter(Boolean).join("_");
    a.href = url; a.download = `jamora-${EXPORT_NAMES[kind] || kind}${suffix ? `_${suffix}` : ""}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (err) {
    let message = "Export impossible pour le moment.";
    try { message = apiError({ response: { data: JSON.parse(await err.response.data.text()) } }); } catch { /* keep the generic message */ }
    toast.error(message);
    return false;
  }
}

export default function ExportDialog({ open, onOpenChange, canExpenses, canContributions }) {
  const kinds = [canExpenses && ["expenses", "Dépenses"], canExpenses && ["preconstitution", "Dépenses avant constitution"],
    canContributions && ["contributions", "Contributions (qui a payé quoi)"], canContributions && ["reimbursements", "Remboursements des avances"]].filter(Boolean);
  const [kind, setKind] = useState(kinds[0]?.[0] || "");
  const [dates, setDates] = useState({ dateFrom: "", dateTo: "" });
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBusy(false); }, [open]);
  const invalid = dates.dateFrom && dates.dateTo && dates.dateFrom > dates.dateTo;

  const run = async () => {
    setBusy(true);
    const ok = await downloadExport(kind === "preconstitution" ? "expenses" : kind,
      kind === "reimbursements" ? dates : { ...dates, status, preconstitution: kind === "preconstitution" });
    setBusy(false);
    if (ok) { toast.success("Export téléchargé"); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="finance-export-dialog">
        <DialogHeader>
          <DialogTitle>Exporter les finances</DialogTitle>
          <DialogDescription>Un fichier CSV qui s'ouvre dans Excel ou Google Sheets. Chaque export est enregistré dans le journal d'activité.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Contenu</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="finance-export-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{kinds.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="export-from">Du</Label>
              <Input id="export-from" type="date" value={dates.dateFrom} onChange={(e) => setDates((d) => ({ ...d, dateFrom: e.target.value }))} className="text-base md:text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="export-to">Au</Label>
              <Input id="export-to" type="date" value={dates.dateTo} onChange={(e) => setDates((d) => ({ ...d, dateTo: e.target.value }))} className="text-base md:text-sm" />
            </div>
          </div>
          <p className="text-xs text-slate-500 -mt-2">
            {{ contributions: "Période sur la date de paiement.", reimbursements: "Période sur la date du remboursement. Les remboursements annulés figurent avec leur motif." }[kind]
              || "Période sur la date de la dépense (ou de son premier paiement)."} Laissez vide pour tout exporter.
          </p>
          {kind !== "reimbursements" && <div className="space-y-1.5">
            <Label>Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Brouillons et soumises</SelectItem>
                <SelectItem value="submitted">Soumises seulement</SelectItem>
                <SelectItem value="draft">Brouillons seulement</SelectItem>
              </SelectContent>
            </Select>
          </div>}
          {invalid && <p role="alert" className="text-sm text-red-700">La date de début est après la date de fin.</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={run} disabled={busy || !kind || invalid} data-testid="finance-export-run">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />} Télécharger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
